import { useCallback, useEffect, useRef, useState } from 'react';

export interface ScrollState {
  /** Viewport dimensions */
  viewportWidth: number;
  viewportHeight: number;
  /** Content dimensions */
  contentWidth: number;
  contentHeight: number;
  /** Current scroll position */
  scrollLeft: number;
  scrollTop: number;
  /** Whether scrollbars are needed */
  hasVerticalScroll: boolean;
  hasHorizontalScroll: boolean;
  /** Thumb sizes (as percentage of track) */
  verticalThumbHeight: number;
  horizontalThumbWidth: number;
  /** Thumb positions (as percentage) */
  verticalThumbTop: number;
  horizontalThumbLeft: number;
}

export interface UseScrollOptions {
  /** Auto-hide scrollbars */
  autoHide?: boolean;
  /** Delay before hiding (ms) */
  autoHideDelay?: number;
  /** Minimum thumb size in pixels */
  minThumbSize?: number;
  /** Callback when scroll changes */
  onScrollChange?: ((position: { x: number; y: number }) => void) | undefined;
}

export interface UseScrollReturn {
  /** Ref to attach to viewport element */
  viewportRef: React.RefObject<HTMLDivElement | null>;
  /** Ref to attach to content wrapper */
  contentRef: React.RefObject<HTMLDivElement | null>;
  /** Current scroll state */
  scrollState: ScrollState;
  /** Whether scrollbars are visible (for auto-hide) */
  isVisible: boolean;
  /** Whether currently dragging */
  isDragging: boolean;
  /** Handlers for vertical scrollbar */
  verticalHandlers: {
    onTrackClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    onThumbMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
    onThumbTouchStart: (e: React.TouchEvent<HTMLDivElement>) => void;
  };
  /** Handlers for horizontal scrollbar */
  horizontalHandlers: {
    onTrackClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    onThumbMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
    onThumbTouchStart: (e: React.TouchEvent<HTMLDivElement>) => void;
  };
  /** Container handlers for auto-hide on mouse enter/leave/move */
  containerHandlers: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onMouseMove: () => void;
  };
  /** Scroll to position programmatically */
  scrollTo: (options: { x?: number; y?: number }) => void;
}

const DEFAULT_MIN_THUMB_SIZE = 30;
const DEFAULT_AUTO_HIDE_DELAY = 1000;

export function useScroll(options: UseScrollOptions = {}): UseScrollReturn {
  const {
    autoHide = false,
    autoHideDelay = DEFAULT_AUTO_HIDE_DELAY,
    minThumbSize = DEFAULT_MIN_THUMB_SIZE,
    onScrollChange,
  } = options;

  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [scrollState, setScrollState] = useState<ScrollState>({
    viewportWidth: 0,
    viewportHeight: 0,
    contentWidth: 0,
    contentHeight: 0,
    scrollLeft: 0,
    scrollTop: 0,
    hasVerticalScroll: false,
    hasHorizontalScroll: false,
    verticalThumbHeight: 0,
    horizontalThumbWidth: 0,
    verticalThumbTop: 0,
    horizontalThumbLeft: 0,
  });

  const [isVisible, setIsVisible] = useState(!autoHide);
  const [isDragging, setIsDragging] = useState(false);

  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragStateRef = useRef<{
    type: 'vertical' | 'horizontal';
    startPos: number;
    startScroll: number;
  } | null>(null);

  // Calculate scroll state
  const updateScrollState = useCallback(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    const viewportWidth = viewport.clientWidth;
    const viewportHeight = viewport.clientHeight;
    const contentWidth = content.scrollWidth;
    const contentHeight = content.scrollHeight;
    const scrollLeft = viewport.scrollLeft;
    const scrollTop = viewport.scrollTop;

    const hasVerticalScroll = contentHeight > viewportHeight;
    const hasHorizontalScroll = contentWidth > viewportWidth;

    // Calculate thumb sizes (as percentage, with minimum)
    let verticalThumbHeight = 0;
    let horizontalThumbWidth = 0;

    if (hasVerticalScroll) {
      const ratio = viewportHeight / contentHeight;
      const minRatio = minThumbSize / viewportHeight;
      verticalThumbHeight = Math.max(ratio, minRatio) * 100;
    }

    if (hasHorizontalScroll) {
      const ratio = viewportWidth / contentWidth;
      const minRatio = minThumbSize / viewportWidth;
      horizontalThumbWidth = Math.max(ratio, minRatio) * 100;
    }

    // Calculate thumb positions (as percentage)
    let verticalThumbTop = 0;
    let horizontalThumbLeft = 0;

    if (hasVerticalScroll) {
      const maxScroll = contentHeight - viewportHeight;
      const scrollRatio = scrollTop / maxScroll;
      const trackSpace = 100 - verticalThumbHeight;
      verticalThumbTop = scrollRatio * trackSpace;
    }

    if (hasHorizontalScroll) {
      const maxScroll = contentWidth - viewportWidth;
      const scrollRatio = scrollLeft / maxScroll;
      const trackSpace = 100 - horizontalThumbWidth;
      horizontalThumbLeft = scrollRatio * trackSpace;
    }

    setScrollState({
      viewportWidth,
      viewportHeight,
      contentWidth,
      contentHeight,
      scrollLeft,
      scrollTop,
      hasVerticalScroll,
      hasHorizontalScroll,
      verticalThumbHeight,
      horizontalThumbWidth,
      verticalThumbTop,
      horizontalThumbLeft,
    });
  }, [minThumbSize]);

  // Track if mouse is inside container
  const isMouseInsideRef = useRef(false);

  // Handle auto-hide visibility
  const showScrollbars = useCallback(() => {
    if (autoHide) {
      setIsVisible(true);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      if (!isDragging) {
        hideTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, autoHideDelay);
      }
    }
  }, [autoHide, autoHideDelay, isDragging]);

  // Hide scrollbars immediately (for mouse leave)
  const hideScrollbars = useCallback(() => {
    if (autoHide && !isDragging) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      setIsVisible(false);
    }
  }, [autoHide, isDragging]);

  // Container mouse handlers
  const handleMouseEnter = useCallback(() => {
    isMouseInsideRef.current = true;
    if (autoHide) {
      setIsVisible(true);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      // Start hide timer
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, autoHideDelay);
    }
  }, [autoHide, autoHideDelay]);

  const handleMouseLeave = useCallback(() => {
    isMouseInsideRef.current = false;
    hideScrollbars();
  }, [hideScrollbars]);

  const handleContainerMouseMove = useCallback(() => {
    if (autoHide && isMouseInsideRef.current) {
      setIsVisible(true);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      if (!isDragging) {
        hideTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, autoHideDelay);
      }
    }
  }, [autoHide, autoHideDelay, isDragging]);

  // Handle scroll event
  const handleScroll = useCallback(() => {
    updateScrollState();
    showScrollbars();

    const viewport = viewportRef.current;
    if (viewport && onScrollChange) {
      onScrollChange({ x: viewport.scrollLeft, y: viewport.scrollTop });
    }
  }, [updateScrollState, showScrollbars, onScrollChange]);

  // Set up ResizeObserver
  useEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });

    resizeObserver.observe(viewport);
    resizeObserver.observe(content);

    // Initial calculation
    updateScrollState();

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateScrollState]);

  // Set up scroll listener
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      viewport.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Vertical track click handler
  const handleVerticalTrackClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const viewport = viewportRef.current;
      const track = e.currentTarget;
      if (!viewport) return;

      const rect = track.getBoundingClientRect();
      const clickY = e.clientY - rect.top;
      const trackHeight = rect.height;
      const clickRatio = clickY / trackHeight;

      const maxScroll = scrollState.contentHeight - scrollState.viewportHeight;
      viewport.scrollTop = clickRatio * maxScroll;
    },
    [scrollState.contentHeight, scrollState.viewportHeight]
  );

  // Horizontal track click handler
  const handleHorizontalTrackClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const viewport = viewportRef.current;
      const track = e.currentTarget;
      if (!viewport) return;

      const rect = track.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const trackWidth = rect.width;
      const clickRatio = clickX / trackWidth;

      const maxScroll = scrollState.contentWidth - scrollState.viewportWidth;
      viewport.scrollLeft = clickRatio * maxScroll;
    },
    [scrollState.contentWidth, scrollState.viewportWidth]
  );

  // Mouse drag handlers
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const viewport = viewportRef.current;
      const dragState = dragStateRef.current;
      if (!viewport || !dragState) return;

      e.preventDefault();

      if (dragState.type === 'vertical') {
        const delta = e.clientY - dragState.startPos;
        const trackHeight = scrollState.viewportHeight;
        const thumbHeight = (scrollState.verticalThumbHeight / 100) * trackHeight;
        const scrollableTrack = trackHeight - thumbHeight;
        const maxScroll = scrollState.contentHeight - scrollState.viewportHeight;

        if (scrollableTrack > 0) {
          const scrollDelta = (delta / scrollableTrack) * maxScroll;
          viewport.scrollTop = Math.max(
            0,
            Math.min(maxScroll, dragState.startScroll + scrollDelta)
          );
        }
      } else {
        const delta = e.clientX - dragState.startPos;
        const trackWidth = scrollState.viewportWidth;
        const thumbWidth = (scrollState.horizontalThumbWidth / 100) * trackWidth;
        const scrollableTrack = trackWidth - thumbWidth;
        const maxScroll = scrollState.contentWidth - scrollState.viewportWidth;

        if (scrollableTrack > 0) {
          const scrollDelta = (delta / scrollableTrack) * maxScroll;
          viewport.scrollLeft = Math.max(
            0,
            Math.min(maxScroll, dragState.startScroll + scrollDelta)
          );
        }
      }
    },
    [scrollState]
  );

  const handleMouseUp = useCallback(() => {
    dragStateRef.current = null;
    setIsDragging(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    // Restart auto-hide timer
    if (autoHide) {
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, autoHideDelay);
    }
  }, [handleMouseMove, autoHide, autoHideDelay]);

  const handleVerticalThumbMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const viewport = viewportRef.current;
      if (!viewport) return;

      dragStateRef.current = {
        type: 'vertical',
        startPos: e.clientY,
        startScroll: viewport.scrollTop,
      };

      setIsDragging(true);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [handleMouseMove, handleMouseUp]
  );

  const handleHorizontalThumbMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const viewport = viewportRef.current;
      if (!viewport) return;

      dragStateRef.current = {
        type: 'horizontal',
        startPos: e.clientX,
        startScroll: viewport.scrollLeft,
      };

      setIsDragging(true);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [handleMouseMove, handleMouseUp]
  );

  // Touch drag handlers
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      const viewport = viewportRef.current;
      const dragState = dragStateRef.current;
      if (!viewport || !dragState || !e.touches[0]) return;

      if (dragState.type === 'vertical') {
        const delta = e.touches[0].clientY - dragState.startPos;
        const trackHeight = scrollState.viewportHeight;
        const thumbHeight = (scrollState.verticalThumbHeight / 100) * trackHeight;
        const scrollableTrack = trackHeight - thumbHeight;
        const maxScroll = scrollState.contentHeight - scrollState.viewportHeight;

        if (scrollableTrack > 0) {
          const scrollDelta = (delta / scrollableTrack) * maxScroll;
          viewport.scrollTop = Math.max(
            0,
            Math.min(maxScroll, dragState.startScroll + scrollDelta)
          );
        }
      } else {
        const delta = e.touches[0].clientX - dragState.startPos;
        const trackWidth = scrollState.viewportWidth;
        const thumbWidth = (scrollState.horizontalThumbWidth / 100) * trackWidth;
        const scrollableTrack = trackWidth - thumbWidth;
        const maxScroll = scrollState.contentWidth - scrollState.viewportWidth;

        if (scrollableTrack > 0) {
          const scrollDelta = (delta / scrollableTrack) * maxScroll;
          viewport.scrollLeft = Math.max(
            0,
            Math.min(maxScroll, dragState.startScroll + scrollDelta)
          );
        }
      }
    },
    [scrollState]
  );

  const handleTouchEnd = useCallback(() => {
    dragStateRef.current = null;
    setIsDragging(false);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);

    // Restart auto-hide timer
    if (autoHide) {
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, autoHideDelay);
    }
  }, [handleTouchMove, autoHide, autoHideDelay]);

  const handleVerticalThumbTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      e.stopPropagation();

      const viewport = viewportRef.current;
      const touch = e.touches[0];
      if (!viewport || !touch) return;

      dragStateRef.current = {
        type: 'vertical',
        startPos: touch.clientY,
        startScroll: viewport.scrollTop,
      };

      setIsDragging(true);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }

      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    },
    [handleTouchMove, handleTouchEnd]
  );

  const handleHorizontalThumbTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      e.stopPropagation();

      const viewport = viewportRef.current;
      const touch = e.touches[0];
      if (!viewport || !touch) return;

      dragStateRef.current = {
        type: 'horizontal',
        startPos: touch.clientX,
        startScroll: viewport.scrollLeft,
      };

      setIsDragging(true);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }

      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    },
    [handleTouchMove, handleTouchEnd]
  );

  // Programmatic scroll
  const scrollTo = useCallback((options: { x?: number; y?: number }) => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    if (options.x !== undefined) {
      viewport.scrollLeft = options.x;
    }
    if (options.y !== undefined) {
      viewport.scrollTop = options.y;
    }
  }, []);

  return {
    viewportRef,
    contentRef,
    scrollState,
    isVisible,
    isDragging,
    verticalHandlers: {
      onTrackClick: handleVerticalTrackClick,
      onThumbMouseDown: handleVerticalThumbMouseDown,
      onThumbTouchStart: handleVerticalThumbTouchStart,
    },
    horizontalHandlers: {
      onTrackClick: handleHorizontalTrackClick,
      onThumbMouseDown: handleHorizontalThumbMouseDown,
      onThumbTouchStart: handleHorizontalThumbTouchStart,
    },
    containerHandlers: {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onMouseMove: handleContainerMouseMove,
    },
    scrollTo,
  };
}
